import { MAIN_URL } from "../../../Configurations/Urls";

export function resolveLogoUrl(value) {
  if (value == null || String(value).trim() === "") return null;
  const s = String(value).trim();
  if (/^https?:\/\//i.test(s)) {
    // Fix legacy/wrong Laravel symlink URLs on this deployment
    return s.replace(
      /\/storage\/logos\//i,
      "/storage/app/public/logos/"
    );
  }
  if (s.startsWith("/")) return `${MAIN_URL}${s}`;
  if (s.startsWith("storage/app/public/") || s.startsWith("org_img/")) {
    return `${MAIN_URL}/${s}`;
  }
  if (s.startsWith("storage/")) {
    return `${MAIN_URL}/storage/app/public/${s.replace(/^storage\//, "")}`;
  }
  return `${MAIN_URL}/storage/app/public/${s.replace(/^\/+/, "")}`;
}

export function displayValue(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).trim();
}

export function formatDisplayDate(value) {
  if (value == null || value === "") return "—";
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB");
    }
  }
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slash) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB");
  }
  return s;
}

export function fmtMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtMoneyWithCurrency(amount, currencyCode) {
  const m = fmtMoney(amount);
  if (m === "—") return "—";
  const c =
    currencyCode != null && String(currencyCode).trim() !== ""
      ? String(currencyCode).trim()
      : "";
  return c ? `${m} ${c}` : m;
}

export function amountInWordsEn(n, currencyCode) {
  const num = Number(n);
  if (!Number.isFinite(num) || num < 0) return "—";
  if (num === 0) return "Zero Only";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const twoDigits = (x) => {
    if (x < 20) return ones[x];
    return `${tens[Math.floor(x / 10)]}${ones[x % 10] ? ` ${ones[x % 10]}` : ""}`.trim();
  };

  const threeDigits = (x) => {
    const h = Math.floor(x / 100);
    const r = x % 100;
    const parts = [];
    if (h > 0) parts.push(`${twoDigits(h)} Hundred`);
    if (r > 0) parts.push(twoDigits(r));
    return parts.join(" ").trim();
  };

  const numberToWords = (x) => {
    if (x === 0) return "Zero";
    const parts = [];
    let n = x;
    const crore = Math.floor(n / 10000000);
    n %= 10000000;
    const lakh = Math.floor(n / 100000);
    n %= 100000;
    const thousand = Math.floor(n / 1000);
    n %= 1000;
    if (crore > 0) parts.push(`${threeDigits(crore)} Crore`);
    if (lakh > 0) parts.push(`${threeDigits(lakh)} Lakh`);
    if (thousand > 0) parts.push(`${threeDigits(thousand)} Thousand`);
    if (n > 0) parts.push(threeDigits(n));
    return parts.join(" ").trim();
  };

  const whole = Math.floor(num);
  const fraction = Math.round((num - whole) * 100);
  let words = numberToWords(whole);
  if (fraction > 0) {
    words += ` and ${numberToWords(fraction)} Paise`;
  }
  const code =
    currencyCode != null && String(currencyCode).trim() !== ""
      ? ` ${String(currencyCode).trim()}`
      : "";
  return `${words}${code} Only`;
}

export function getReceiptAmountColumnLabel(data) {
  if (!data) return "Amount";

  let code = data.studentCurrencyCode;
  if (!code || code === "—") {
    const formatted = String(data.amountFormatted || "");
    const match = formatted.match(/\s([A-Za-z]{3})\s*$/);
    if (match) code = match[1].toUpperCase();
  }

  return code && code !== "—" ? `Amount (${code})` : "Amount";
}

export function normalizeReceiptData(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    organizationName: displayValue(raw.organization_name),
    logoUrl:
      raw.logo_data_url || resolveLogoUrl(raw.logo_url),
    phone: displayValue(raw.phone),
    email: displayValue(raw.email),
    website: displayValue(raw.website),
    receiptNo: displayValue(raw.receipt_no),
    receiptDate: formatDisplayDate(raw.receipt_date),
    studentName: displayValue(raw.student_name),
    studentId: displayValue(raw.student_id),
    admissionNo: displayValue(raw.admission_no),
    financialYear: displayValue(raw.financial_year),
    particulars: displayValue(raw.particulars),
    studentCurrencyCode: displayValue(raw.student_currency_code),
    amountFormatted:
      raw.amount_with_currency != null
        ? displayValue(raw.amount_with_currency)
        : fmtMoneyWithCurrency(raw.amount, raw.student_currency_code),
    amountInWords: displayValue(
      raw.amount_in_words ??
        amountInWordsEn(raw.amount, raw.student_currency_code)
    ),
    paymentMode: displayValue(raw.payment_mode),
    bank: displayValue(raw.bank),
    paymentStatus: displayValue(raw.payment_status),
    footerNote: raw.footer_note || "This is a system generated receipt.",
    pageLabel: raw.page_label || "-- 1 of 1 --",
  };
}

function studentNameFromRecord(student) {
  if (!student) return null;
  const parts = [student.first_name, student.middle_name, student.last_name].filter(
    (x) => x && String(x).trim() !== ""
  );
  if (parts.length) return parts.join(" ");
  if (student.full_name) return String(student.full_name);
  return null;
}

function financialYearFromDate(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  let d = null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  } else {
    d = new Date(s);
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  return month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
}

function resolveOrganizationFromAdmission(admission) {
  return admission?.organization ?? admission?.Organization ?? {};
}

function resolveOrganizationProfile(org) {
  if (!org || typeof org !== "object") return {};
  return (
    org.profile ??
    org.identity_profile ??
    org.identityProfile ??
    org.business_identity_profile ??
    {}
  );
}

function profileContactFields(profile) {
  if (!profile || typeof profile !== "object") {
    return { phone: null, email: null, website: null, logo_url: null };
  }
  return {
    phone: profile.phone ?? profile.phone_no ?? profile.contact_phone ?? null,
    email: profile.email ?? profile.contact_email ?? null,
    website: profile.website ?? profile.website_url ?? null,
    logo_url: profile.logo_url ?? null,
  };
}

export async function fetchOrganizationProfileContacts(organizationId) {
  if (!organizationId) {
    return { phone: null, email: null, website: null, logo_url: null };
  }

  const { default: axios } = await import("axios");
  const { MAIN_URL } = await import("../../../Configurations/Urls");

  try {
    const res = await axios.get(
      `${MAIN_URL}/api/organizations/${organizationId}/business-identity-profile`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return profileContactFields(res.data?.profile ?? res.data ?? {});
  } catch (err) {
    console.warn("Organization profile fetch failed:", err);
    return { phone: null, email: null, website: null, logo_url: null };
  }
}

function resolveBatchCode(admission) {
  const admissionId = admission?.organization_ems_admission_id;
  const batches =
    admission?.student?.studnet_batches ??
    admission?.student?.StudnetBatches ??
    [];
  if (!Array.isArray(batches) || batches.length === 0) return null;

  const match =
    batches.find(
      (row) =>
        admissionId != null &&
        String(row?.organization_ems_admission_id) === String(admissionId)
    ) ?? batches[0];

  return match?.batch?.batch_code ?? match?.batch_code ?? null;
}

export function buildReceiptDataFromLocal(admission, sf, inst, profileContacts) {
  if (!admission || !sf) return null;

  const org = resolveOrganizationFromAdmission(admission);
  const profile = {
    ...resolveOrganizationProfile(org),
    ...(profileContacts ?? {}),
  };
  const trainingProgram =
    admission?.training_program ?? admission?.trainingProgram ?? {};
  const studentCurrency =
    sf?.student_currency_code ??
    inst?.student_currency_code ??
    admission?.currency_code;
  const amount = sf?.amount_paid_student_currency;
  const paymentDate = sf?.payment_date;
  const installmentDate = inst?.due_date ?? paymentDate;

  return normalizeReceiptData({
    organization_name: org?.organization_name ?? org?.organization_short_name,
    logo_url: profile?.logo_url,
    phone: profile?.phone,
    email: profile?.email,
    website: profile?.website,
    receipt_no: sf?.payment_number,
    receipt_date: paymentDate,
    student_name: studentNameFromRecord(admission?.student),
    student_id: admission?.student?.student_id,
    admission_no: admission?.admission_number,
    batch_code: resolveBatchCode(admission),
    installment_date: installmentDate,
    financial_year: financialYearFromDate(paymentDate ?? installmentDate),
    particulars:
      trainingProgram?.training_program_name ?? "Training Program Fee",
    amount,
    student_currency_code: studentCurrency,
    amount_in_words: amountInWordsEn(amount, studentCurrency),
    payment_mode: sf?.payment_mode,
    bank: null,
    payment_status: sf?.payment_status,
  });
}

export async function fetchFeeReceiptData(studentFeeId, organizationId) {
  const { default: axios } = await import("axios");
  const { MAIN_URL } = await import("../../../Configurations/Urls");

  const res = await axios.get(
    `${MAIN_URL}/api/ems/students-fees/${studentFeeId}/receipt`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      params: organizationId ? { organization_id: organizationId } : undefined,
    }
  );

  const payload = res.data?.data ?? res.data;
  return normalizeReceiptData(payload);
}

export async function getReceiptForDownload(
  admission,
  sf,
  inst,
  organizationId
) {
  const feeId = sf?.organization_ems_student_fee_id;
  const orgId =
    organizationId ??
    admission?.organization_id ??
    admission?.organization?.organization_id ??
    admission?.Organization?.organization_id;

  if (feeId) {
    try {
      const fromApi = await fetchFeeReceiptData(feeId, orgId);
      if (fromApi) return fromApi;
    } catch (err) {
      console.warn("Fee receipt API failed, using local data:", err);
    }
  }

  const org = resolveOrganizationFromAdmission(admission);
  let profileContacts = profileContactFields(resolveOrganizationProfile(org));
  const needsProfileFetch = ["phone", "email", "website", "logo_url"].some(
    (key) =>
      profileContacts[key] == null || String(profileContacts[key]).trim() === ""
  );

  if (needsProfileFetch && orgId) {
    const fetched = await fetchOrganizationProfileContacts(orgId);
    profileContacts = {
      phone: profileContacts.phone ?? fetched.phone,
      email: profileContacts.email ?? fetched.email,
      website: profileContacts.website ?? fetched.website,
      logo_url: profileContacts.logo_url ?? fetched.logo_url,
    };
  }

  return buildReceiptDataFromLocal(admission, sf, inst, profileContacts);
}
